import type {
  ChartPanel,
  Dimension,
  FactRecord,
  Separator,
  StatisticFilters,
  TableRow,
} from '../types'

export const DIMENSIONS: { value: Dimension; label: string }[] = [
  { value: 'month', label: '월' },
  { value: 'gender', label: '성별' },
  { value: 'device', label: '디바이스' },
  { value: 'ageGroup', label: '연령대' },
  { value: 'keyword', label: '키워드' },
]

export const AGE_GROUPS = ['0-12', '13-19', '20-24', '25-29', '30-39', '40-49', '50-']
export const DEVICES: FactRecord['device'][] = ['pc', 'mobile']
export const GENDERS: FactRecord['gender'][] = ['m', 'f']

export function dimensionLabel(value: Dimension) {
  return DIMENSIONS.find((item) => item.value === value)?.label ?? value
}

export function valueLabel(dimension: Dimension, value: string) {
  if (value === '') return '알 수 없음'
  if (dimension === 'gender') return value === 'm' ? '남성' : '여성'
  if (dimension === 'device') return value === 'pc' ? 'PC' : '모바일'
  if (dimension === 'ageGroup') return value === '50-' ? '50세 이상' : `${value.replace('-', '–')}세`
  return value
}

function dimensionValue(record: FactRecord, dimension: Dimension) {
  return record[dimension]
}

function orderedValues(values: Iterable<string>, dimension: Dimension) {
  const unique = [...new Set(values)]
  const order =
    dimension === 'ageGroup'
      ? AGE_GROUPS
      : dimension === 'gender'
        ? GENDERS
        : dimension === 'device'
          ? DEVICES
          : []

  return unique.sort((left, right) => {
    const leftOrder = order.indexOf(left as never)
    const rightOrder = order.indexOf(right as never)
    if (leftOrder >= 0 || rightOrder >= 0) return leftOrder - rightOrder
    return left.localeCompare(right, 'ko', { numeric: true })
  })
}

export function applyFilters(records: FactRecord[], filters: StatisticFilters) {
  const keywordSet = new Set(filters.keywords)
  const ageSet = new Set(filters.ageGroups)
  const deviceSet = new Set(filters.devices)
  const genderSet = new Set(filters.genders)

  return records.filter(
    (record) =>
      record.month >= filters.startMonth &&
      record.month <= filters.endMonth &&
      keywordSet.has(record.keyword) &&
      ageSet.has(record.ageGroup) &&
      deviceSet.has(record.device) &&
      genderSet.has(record.gender),
  )
}

export function aggregateStatistics(
  records: FactRecord[],
  xAxis: Dimension,
  seriesDimension: Dimension,
  separator: Separator,
): { panels: ChartPanel[]; rows: TableRow[] } {
  const panelGroups = new Map<string, FactRecord[]>()

  for (const record of records) {
    const panelKey = separator === 'none' ? 'all' : dimensionValue(record, separator)
    const group = panelGroups.get(panelKey) ?? []
    group.push(record)
    panelGroups.set(panelKey, group)
  }

  const panels: ChartPanel[] = []
  const rows: TableRow[] = []

  const panelEntries = [...panelGroups.entries()].sort(([left], [right]) =>
    separator === 'none'
      ? 0
      : orderedValues([left, right], separator)[0] === left
        ? -1
        : 1,
  )

  for (const [panelKey, panelRecords] of panelEntries) {
    const xValues = orderedValues(
      panelRecords.map((record) => dimensionValue(record, xAxis)),
      xAxis,
    )
    const seriesValues = orderedValues(
      panelRecords.map((record) => dimensionValue(record, seriesDimension)),
      seriesDimension,
    )
    const sums = new Map<string, number>()

    for (const record of panelRecords) {
      const key = `${dimensionValue(record, xAxis)}\u0000${dimensionValue(record, seriesDimension)}`
      sums.set(key, (sums.get(key) ?? 0) + record.impression)
    }

    const panelTitle =
      separator === 'none' ? '전체 데이터' : valueLabel(separator, panelKey)
    const series = seriesValues.map((seriesKey) => ({
      key: seriesKey,
      label: valueLabel(seriesDimension, seriesKey),
      values: xValues.map((xValue) => sums.get(`${xValue}\u0000${seriesKey}`) ?? 0),
    }))

    for (const seriesItem of series) {
      xValues.forEach((xValue, index) => {
        rows.push({
          panel: panelTitle,
          xAxis: valueLabel(xAxis, xValue),
          series: seriesItem.label,
          impression: seriesItem.values[index],
        })
      })
    }

    panels.push({
      key: panelKey,
      title: panelTitle,
      labels: xValues.map((value) => valueLabel(xAxis, value)),
      series,
      total: panelRecords.reduce((sum, record) => sum + record.impression, 0),
    })
  }

  return { panels, rows }
}
